/**
 * Shared formatting helpers for the Fleet HUD components.
 * All timestamps arrive as strings (the backend serializes Mongo datetimes
 * with `default=str`), so parsing goes through Date.parse with a fallback for
 * the "YYYY-MM-DD HH:MM:SS.ffffff" shape str(datetime) produces.
 */

/** Naive timestamp — date + time with NO timezone designator (str(datetime)
 * shape, space- or T-separated, optional fractional seconds). */
const NAIVE_TS = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export function parseTs(ts?: string | null): number | null {
  if (!ts) return null;
  const s = ts.trim();
  // Backend Mongo datetimes are UTC but serialize NAIVE ('2026-08-13
  // 17:02:10.948000' — no Z). The old fallback normalized to UTC only when
  // Date.parse returned NaN, but V8 parses the space form successfully AS
  // LOCAL TIME — so the fallback never ran and every session/run timestamp
  // was displaced by the viewer's UTC offset (the 378-minute replay of a
  // 78-minute run, 2026-08-14: journal is ISO-Z, sessions are naive, and the
  // two clocks were 5h apart in Chicago). Detect naive shapes FIRST and pin
  // them to UTC; anything carrying its own designator parses as-is.
  const ms = NAIVE_TS.test(s) ? Date.parse(s.replace(' ', 'T') + 'Z') : Date.parse(s);
  return Number.isNaN(ms) ? null : ms;
}

/** Seconds → compact "3m" / "47m" / "2h 5m" / "1d 3h". Null-safe. */
export function formatElapsedSeconds(seconds?: number | null): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 === 0 ? `${h}h` : `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return h % 24 === 0 ? `${d}d` : `${d}d ${h % 24}h`;
}

/** Elapsed time between an ISO/str timestamp and now, compact form. */
export function formatElapsedSince(ts?: string | null, nowMs: number = Date.now()): string {
  const start = parseTs(ts);
  if (start === null) return '—';
  return formatElapsedSeconds((nowMs - start) / 1000);
}

/** Duration between two timestamps, compact form. */
export function formatDuration(startTs?: string | null, endTs?: string | null): string {
  const start = parseTs(startTs);
  const end = parseTs(endTs);
  if (start === null || end === null) return '—';
  return formatElapsedSeconds((end - start) / 1000);
}
